import React, { forwardRef } from 'react';
import type { ForwardedRef, ReactNode } from 'react';

import { Control } from '@vlabs/uikit';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import type { RequiredPopoverProps } from '..';
import { buildTestid } from '../../../lib/utils';
import { Popover } from '../popover/Popover';
import type { PopoverProps } from '../popover/Popover';

import './PopoverWithButton.sass';

export interface PopoverWithButtonProps extends PopoverProps, RequiredPopoverProps {
  onClickReset: VoidFunction;
  isButtonDisabled?: boolean | undefined;
  title?: ReactNode | undefined;
}

const displayName = 'PopoverWithButton';
const testid = buildTestid(displayName);

function PopoverWithButton(
  {
    onClickReset,
    children,
    style,
    isButtonDisabled = undefined,
    title = undefined,
    'data-testid': dataTestId = undefined,
    ...props
  }: PopoverWithButtonProps,
  ref: ForwardedRef<HTMLDivElement>,
): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Popover
      {...props}
      data-testid={dataTestId}
      ref={ref}
      rootProps={{
        ...props?.rootProps,
        style,
      }}
    >
      <div
        className={cn('FilterPopupContent', { FilterPopupContent_withTitle: !!title })}
        {...testid('Content', dataTestId)}
      >
        {!!title && (
          <h6
            className="FilterPopupTitle"
            {...testid('Title', dataTestId)}
          >
            {title}
          </h6>
        )}
        {children}
        {!!onClickReset && (
          <React.Fragment>
            <div className="PopoverWithButton__Divider" />
            <Control.Button
              className="PopoverWithButton__ResetButton"
              disabled={isButtonDisabled}
              kind="negative"
              onClick={onClickReset}
              size="small"
              variant="filled"
              {...testid('ResetButton', dataTestId)}
            >
              {t('button.clear')}
            </Control.Button>
          </React.Fragment>
        )}
      </div>
    </Popover>
  );
}

PopoverWithButton.displayName = displayName;

const PopoverWithButtonWithRef = forwardRef(PopoverWithButton);
export {
  PopoverWithButtonWithRef as PopoverWithButton,
};
